import { getInvoiceSeller, INVOICE_VAT_RATE } from '../constants/invoiceSeller';
import { BILLING_COUNTRY_OPTIONS } from './billingPhone';

/** @typedef {{ title: string; qty: number; unitPriceCents: number; lineTotalCents: number }} InvoiceLineItem */

/** @typedef {{
 *   invoiceNumber: string;
 *   issueDate: string;
 *   orderId: string;
 *   currency: string;
 *   seller: ReturnType<typeof getInvoiceSeller>;
 *   buyer: import('./orderInvoiceBilling').OrderInvoiceBilling;
 *   buyerCountryLabel: string;
 *   items: InvoiceLineItem[];
 *   subtotalCents: number;
 *   vatRate: number;
 *   vatAmountCents: number;
 *   totalCents: number;
 *   paymentStatus: string;
 * }} OrderInvoiceData */

const DEFAULT_INVOICE_CURRENCY = 'EUR';

/**
 * @param {number} cents
 * @param {string} currency
 * @returns {string}
 */
export function formatInvoiceMoney(cents, currency = DEFAULT_INVOICE_CURRENCY) {
  const normalized = String(currency || DEFAULT_INVOICE_CURRENCY).trim().toUpperCase();
  const safeCents = Number.isFinite(Number(cents)) ? Number(cents) : 0;
  const candidate = /^[A-Z]{3}$/.test(normalized) ? normalized : DEFAULT_INVOICE_CURRENCY;

  try {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: candidate,
    }).format(safeCents / 100);
  } catch {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: DEFAULT_INVOICE_CURRENCY,
    }).format(safeCents / 100);
  }
}

/**
 * @param {string | undefined | null} countryCode
 * @returns {string}
 */
export function getInvoiceCountryLabel(countryCode) {
  if (!countryCode) return '';
  const normalized = String(countryCode).trim().toUpperCase();
  if (!normalized) return '';
  const match = BILLING_COUNTRY_OPTIONS.find((option) => option.code === normalized);
  if (match) {
    return match.label.replace(/\s*\(\+\d+\)\s*$/, '').trim();
  }
  return normalized;
}

/**
 * @param {string | null | undefined} orderId
 * @param {Date} [issueDate=new Date()]
 * @returns {string}
 */
export function buildInvoiceNumber(orderId, issueDate = new Date()) {
  const suffix = String(orderId || '00000000').slice(-8).toUpperCase();
  return `F-${issueDate.getFullYear()}-${suffix}`;
}

/**
 * @param {Record<string, string | number | boolean | Array<Record<string, string | number | boolean>> | undefined> | null | undefined} order
 * @returns {InvoiceLineItem[]}
 */
function buildInvoiceLineItems(order) {
  const rawItems = Array.isArray(order?.items) ? order.items : [];

  if (rawItems.length > 0) {
    return rawItems.map((item) => {
      const qty = Number(item.qty) > 0 ? Number(item.qty) : 1;
      const unitPriceCents = Number(item.price) || 0;
      return {
        title: String(item.title || 'Curso'),
        qty,
        unitPriceCents,
        lineTotalCents: unitPriceCents * qty,
      };
    });
  }

  const totalCents = Number(order?.amount) || 0;
  if (totalCents <= 0) {
    return [];
  }

  return [{
    title: 'Pedido de formación online',
    qty: 1,
    unitPriceCents: totalCents,
    lineTotalCents: totalCents,
  }];
}

/**
 * @param {number} totalCents
 * @param {number} vatRate
 * @returns {{ subtotalCents: number; vatAmountCents: number }}
 */
export function splitVatFromTotal(totalCents, vatRate = INVOICE_VAT_RATE) {
  if (vatRate <= 0) {
    return { subtotalCents: totalCents, vatAmountCents: 0 };
  }

  const subtotalCents = Math.round(totalCents / (1 + vatRate / 100));
  return {
    subtotalCents,
    vatAmountCents: totalCents - subtotalCents,
  };
}

/**
 * @param {{
 *   order: Record<string, string | number | boolean | Array<Record<string, string | number | boolean>> | undefined> | null | undefined;
 *   orderId: string | null | undefined;
 *   billing: import('./orderInvoiceBilling').OrderInvoiceBilling;
 *   seller?: ReturnType<typeof getInvoiceSeller>;
 *   vatRate?: number;
 * }} params
 * @returns {OrderInvoiceData}
 */
export function buildOrderInvoiceData({
  order,
  orderId,
  billing,
  seller = getInvoiceSeller(),
  vatRate = INVOICE_VAT_RATE,
}) {
  const items = buildInvoiceLineItems(order);
  const totalCents = items.reduce((sum, item) => sum + item.lineTotalCents, 0);
  const { subtotalCents, vatAmountCents } = splitVatFromTotal(totalCents, vatRate);
  const currency = String(order?.currency || 'EUR');
  const rawTimestamp = order?.paidAt || order?.updatedAt || order?.createdAt;
  const parsedIssueDate = rawTimestamp ? new Date(String(rawTimestamp)) : new Date();
  const issueDate = Number.isNaN(parsedIssueDate.getTime()) ? new Date() : parsedIssueDate;

  return {
    invoiceNumber: buildInvoiceNumber(orderId, issueDate),
    issueDate: issueDate.toLocaleDateString('es-ES'),
    orderId: String(orderId || ''),
    currency,
    seller,
    buyer: billing,
    buyerCountryLabel: getInvoiceCountryLabel(billing.country),
    items,
    subtotalCents,
    vatRate,
    vatAmountCents,
    totalCents,
    paymentStatus: order?.status === 'paid' ? 'Pagado' : 'Pendiente',
  };
}
